// Settings → Billing, for the owner of a business that subscribes: the plan, what it costs, how
// many people and stores it covers, changing the plan or the number of locations (Stripe changes
// first, then the limits here), cancelling at the end of the month, and Stripe's own page for the
// card, invoices and receipts. This is Store OS's bill for the shop, not the shop's customers' payments.
import { useEffect, useState } from 'react';
import { get, post } from '../lib/api';
import { useLoad, useRoute } from '../lib/hooks';
import { usePlan } from '../lib/plan';
import { Badge, Button, Confirm, Field, Spinner, useAction } from '../ui/kit';
import { Icon } from '../ui/icons';
import { formatCents } from '../../shared/money';
import { formatDateTime } from '../../shared/format';
import { FEATURE_LABELS, PLANS, PLAN_IDS, monthlyTotal, usersAllowed, type PlanFeature, type PlanId } from '../../shared/plans';

interface BillingView {
  business: { id: string; name: string; industry: string; industry_name: string; status: string };
  plan: { id: PlanId; name: string; price_cents: number; users_per_location: number; features: PlanFeature[] } | null;
  locations_paid: number;
  monthly_total_cents: number | null;
  users: { used: number; allowed: number | null };
  locations: { used: number; allowed: number };
  subscription: { status: string; renews_at: number | null; cancel_at_period_end: boolean } | null;
  test_mode: boolean;
}

const STATUS: Record<string, { label: string; tone: 'green' | 'amber' | 'red' | 'plain' }> = {
  active: { label: 'Active', tone: 'green' },
  past_due: { label: 'Payment didn’t go through', tone: 'amber' },
  canceled: { label: 'Ended', tone: 'red' },
  suspended: { label: 'Paused', tone: 'red' },
};

export function BillingTab() {
  const route = useRoute();
  const restarted = route.query.get('restarted') === '1';
  const view = useLoad<BillingView>(() => get('/api/platform/billing'), []);
  const plan = usePlan();
  const { run, busy } = useAction();
  const [pick, setPick] = useState<PlanId>('growth');
  const [locations, setLocations] = useState(1);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const v = view.data;
  useEffect(() => {
    if (!v) return;
    if (v.plan) setPick(v.plan.id);
    setLocations(v.locations_paid);
  }, [v?.plan?.id, v?.locations_paid]);
  // Back from Stripe after starting again: check every few seconds until Stripe's events open the business.
  useEffect(() => {
    if (!restarted || !v || v.business.status !== 'canceled') return;
    const t = setTimeout(() => { view.reload(); plan.reload(); }, 3_000);
    return () => clearTimeout(t);
  }, [restarted, v]);
  if (view.error) return <div className="banner warn"><Icon.info /><div>{view.error.message}</div></div>;
  if (!v) return <Spinner />;
  const st = STATUS[v.business.status] ?? { label: v.business.status, tone: 'plain' as const };
  const changed = !!v.plan && (pick !== v.plan.id || locations !== v.locations_paid);
  const newTotal = monthlyTotal(pick, locations);
  const change = async () => {
    const r = await run(() => post<BillingView>('/api/platform/billing/change', { plan: pick, locations }), { success: `Changed to ${PLANS[pick].name} with ${locations} location${locations === 1 ? '' : 's'}.` });
    if (r) { view.setData(r); plan.reload(); }
  };
  const setCancel = async (cancel: boolean) => {
    const r = await run(() => post<BillingView>('/api/platform/billing/cancel', { cancel }), { success: cancel ? 'Your subscription ends at the end of this billing month.' : 'Your subscription continues.' });
    setConfirmCancel(false);
    if (r) { view.setData(r); plan.reload(); }
  };
  const restart = async () => {
    const r = await run(() => post<{ checkout_url: string }>('/api/platform/billing/restart', { plan: pick, locations }));
    if (r?.checkout_url) window.location.assign(r.checkout_url);
  };
  const portal = async () => {
    const r = await run(() => post<{ url: string }>('/api/platform/billing/portal'));
    if (r?.url) window.location.assign(r.url);
  };
  const sub = v.subscription;
  // Buying phones and trade-ins are for phone shops.
  const shown = (features: PlanFeature[]) => features.filter((f) => f !== 'buybacks' || v.business.industry === 'cellphone');
  return (
    <div className="stack">
      {v.test_mode ? <div className="banner"><Icon.info /><div><div className="strong">Test mode</div><div className="small">Billing runs on Stripe’s test mode: no real charges. Test card: 4242 4242 4242 4242.</div></div></div> : null}
      <div className="card stack">
        <div className="spread wrap-sm">
          <h2>{v.plan ? `${v.plan.name} plan` : 'No plan'}</h2>
          <Badge tone={st.tone}>{st.label}</Badge>
        </div>
        <dl className="kv">
          <dt>Monthly</dt><dd className="strong">{v.monthly_total_cents != null ? formatCents(v.monthly_total_cents) : '—'}{v.plan ? <span className="small muted"> · {formatCents(v.plan.price_cents)} × {v.locations_paid} location{v.locations_paid === 1 ? '' : 's'}</span> : null}</dd>
          <dt>Locations</dt><dd>{v.locations.used} open of {v.locations.allowed} paid</dd>
          <dt>Named users</dt><dd>{v.users.used} of {v.users.allowed ?? '—'}{v.plan ? <span className="small muted"> ({v.plan.users_per_location} per location; the owner counts)</span> : null}</dd>
          <dt>{sub?.cancel_at_period_end ? 'Ends on' : 'Renews on'}</dt><dd>{sub?.renews_at ? formatDateTime(sub.renews_at) : '—'}</dd>
          <dt>Kind of shop</dt><dd>{v.business.industry_name}</dd>
        </dl>
        {v.plan ? (
          <div className="small muted">
            Included: sales, {v.business.industry === 'auto' ? 'work orders' : 'repair tickets'}, inventory, customers, daily reports, staff codes and printing.
            {shown(v.plan.features).length ? ` Plus ${shown(v.plan.features).map((f) => FEATURE_LABELS[f].toLowerCase()).join(', ')}.` : ''}
          </div>
        ) : null}
        <div className="row-wrap">
          <Button icon="card" busy={busy} onClick={portal}>Card, invoices and receipts</Button>
          {sub && v.business.status !== 'canceled' ? (
            sub.cancel_at_period_end
              ? <Button variant="primary" busy={busy} onClick={() => setCancel(false)}>Keep my subscription</Button>
              : <Button variant="ghost" onClick={() => setConfirmCancel(true)}>Cancel subscription</Button>
          ) : null}
        </div>
        {v.business.status === 'canceled' ? <p className="small">The subscription has ended. Everything is kept: you can still see it and download a full copy (Business & receipts → Backup). Pick a plan below to start again.</p> : null}
      </div>

      {v.business.status === 'canceled' ? (
        <div className="card stack">
          <h2>Start again</h2>
          {restarted ? <div className="banner"><Icon.info /><div>Waiting for Stripe to confirm the payment. This page updates by itself.</div></div> : null}
          <p className="small muted">You pay on Stripe’s page. Everything you had is still here and opens again once Stripe confirms the payment.</p>
          <PlanPicker pick={pick} onPick={setPick} current={null} name="restart-plan" phones={v.business.industry === 'cellphone'} />
          <div className="form-grid">
            <Field label="Locations" hint={`${v.locations.used} open`}>
              <input className="input" type="number" min={Math.max(1, v.locations.used)} max={100} value={locations} onChange={(e) => setLocations(Math.max(1, Math.min(100, Math.floor(Number(e.target.value) || 1))))} />
            </Field>
            <Field label="Monthly total">
              <div className="input readonly strong">{formatCents(newTotal)} <span className="small muted">· {usersAllowed(pick, locations)} named users</span></div>
            </Field>
          </div>
          <div><Button variant="primary" busy={busy} onClick={restart}>Continue to payment</Button></div>
        </div>
      ) : null}

      {v.plan && v.business.status !== 'canceled' ? (
        <div className="card stack">
          <h2>Change plan or locations</h2>
          <p className="small muted">Changes start now; Stripe works out the difference on the next bill. You can’t go below what’s in use: turn people off in Employees or close a store in Stores & tax first.</p>
          <PlanPicker pick={pick} onPick={setPick} current={v.plan.id} name="billing-plan" phones={v.business.industry === 'cellphone'} />
          <div className="form-grid">
            <Field label="Locations" hint={`${v.locations.used} open now`}>
              <input className="input" type="number" min={Math.max(1, v.locations.used)} max={100} value={locations} onChange={(e) => setLocations(Math.max(1, Math.min(100, Math.floor(Number(e.target.value) || 1))))} />
            </Field>
            <Field label="New monthly total">
              <div className="input readonly strong">{formatCents(newTotal)} <span className="small muted">· {usersAllowed(pick, locations)} named users</span></div>
            </Field>
          </div>
          <div><Button variant="primary" busy={busy} disabled={!changed} onClick={change}>{changed ? `Change to ${PLANS[pick].name}, ${locations} location${locations === 1 ? '' : 's'}` : 'No change'}</Button></div>
        </div>
      ) : null}

      <p className="small muted">This is your Store OS bill. Your customers’ payments at the counter are separate and never go through it.</p>
      {confirmCancel ? (
        <Confirm title="Cancel your subscription?" confirmLabel="Cancel at the end of the month" danger busy={busy} onConfirm={() => setCancel(true)} onClose={() => setConfirmCancel(false)}>
          <p>Store OS keeps working until {sub?.renews_at ? formatDateTime(sub.renews_at) : 'the end of this billing month'}. After that nothing new can be saved, but you can still sign in to see and download everything. You can change your mind before then.</p>
        </Confirm>
      ) : null}
    </div>
  );
}

function PlanPicker({ pick, onPick, current, name, phones }: { pick: PlanId; onPick: (p: PlanId) => void; current: PlanId | null; name: string; phones: boolean }) {
  return (
    <div className="plan-grid" role="radiogroup" aria-label="Plan">
      {PLAN_IDS.map((id) => {
        const p = PLANS[id];
        return (
          <label key={id} className={`plan-card${pick === id ? ' on' : ''}`}>
            <input type="radio" name={name} value={id} checked={pick === id} onChange={() => onPick(id)} />
            <span className="plan-name">{p.name}{current === id ? <span className="small muted"> · now</span> : null}</span>
            <span className="plan-price">{formatCents(p.price_cents)}<span className="small muted"> /location /month</span></span>
            <span className="small">{p.users_per_location} named users per location</span>
            <ul className="plan-feats small">{p.features.length ? p.features.filter((x) => phones || x !== 'buybacks').map((x) => <li key={x}>{FEATURE_LABELS[x]}</li>) : <li>Everything every plan has</li>}</ul>
          </label>
        );
      })}
    </div>
  );
}

